import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse, Divider, Skeleton, UnstyledButton } from '@mantine/core';
import { Link } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import { ChevronDown, ChevronUp } from 'tabler-icons-react';
import AppFlexbox from '../../common/AppFlexbox';
import AppImage from '../../common/AppImage';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import { PAYMENT_PROVIDER_LINE_ITEM_TYPES } from '../../../config/constants';

const getLineItemInfo = (lineItem, regCart) => {
  const regCartProduct =
    lineItem.lineItemType ===
    PAYMENT_PROVIDER_LINE_ITEM_TYPES.registration.value
      ? regCart.regCartProducts?.find(
          (p) =>
            p.regFormSubmission.pkRegFormSubmission === lineItem.entityId &&
            p.regProduct.pkRegProduct ===
              p.regFormSubmission.regAssociationDivisionForm.fkRegProduct
        )
      : null;

  if (regCartProduct) {
    const { regFormSubmission } = regCartProduct;
    const divisionForm = regFormSubmission.regAssociationDivisionForm;

    return {
      image: regCartProduct.regProduct.regAssociation?.logoImageUrl,
      name:
        divisionForm?.titleOverride ||
        divisionForm?.regForm?.name ||
        lineItem.productName,
      description: [
        regFormSubmission.regPerson
          ? `${regFormSubmission.regPerson.firstName} ${regFormSubmission.regPerson.lastName}`
          : null,
        regFormSubmission.regAssociationDivisionOverride?.name ??
          divisionForm?.regAssociationDivision?.name
      ]
        .filter((d) => d)
        .join(' • '),
      type: 'Registration'
    };
  }

  const regCartMerchPackage =
    lineItem.lineItemType === PAYMENT_PROVIDER_LINE_ITEM_TYPES.merch.value
      ? regCart.regCartMerchPackages?.find(
          (p) => p.pkRegCartMerchPackage === lineItem.entityId
        )
      : null;

  if (regCartMerchPackage) {
    return {
      image: regCartMerchPackage.merchPackage.merchPackageImages[0]?.image,
      name: regCartMerchPackage.merchPackage.name,
      description: regCartMerchPackage.merchPackage.description ?? '',
      type: 'Merchandise'
    };
  }

  return {
    image: lineItem.variantImage || lineItem.previewImage,
    name: lineItem.productName,
    description: lineItem.subDescription ?? '',
    type:
      lineItem.lineItemType === PAYMENT_PROVIDER_LINE_ITEM_TYPES.ecommerce.value
        ? 'Shop'
        : null
  };
};

const PaymentPlanOrderSummary = ({ isLoading, pkRegCart, regCart }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const lineItems = regCart?.cart?.lineItems ?? [];

  return (
    <AppStack style={{ gap: 8 }}>
      <AppFlexbox
        style={{
          gap: 12,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap'
        }}
      >
        <UnstyledButton
          disabled={isLoading || lineItems.length === 0}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <AppFlexbox style={{ gap: 6, alignItems: 'center' }}>
            <AppText style={{ fontSize: 13, color: '#666' }}>
              {isLoading
                ? 'What this plan covers'
                : `What this plan covers (${lineItems.length})`}
            </AppText>
            {!isLoading &&
              lineItems.length > 0 &&
              (isExpanded ? (
                <ChevronUp color="#666" size={16} />
              ) : (
                <ChevronDown color="#666" size={16} />
              ))}
          </AppFlexbox>
        </UnstyledButton>

        {pkRegCart && (isLoading || regCart) && (
          <AppText
            component={Link}
            style={{
              fontSize: 13,
              textDecoration: 'none',
              color: 'dodgerblue',
              whiteSpace: 'nowrap'
            }}
            to={`/orders/${pkRegCart}`}
            weight={500}
          >
            View order details
          </AppText>
        )}
      </AppFlexbox>

      {isLoading ? (
        <AppStack style={{ gap: 10 }}>
          <Skeleton height={40} />
          <Skeleton height={40} />
        </AppStack>
      ) : lineItems.length === 0 ? (
        <AppText style={{ fontSize: 13, color: '#666' }}>
          Order details are not available for this plan.
        </AppText>
      ) : (
        <Collapse in={isExpanded}>
          <AppStack style={{ gap: 0 }}>
            {lineItems.map((lineItem, index) => {
              const info = getLineItemInfo(lineItem, regCart);

              return (
                <React.Fragment
                  key={`${lineItem.entityId}-${lineItem.lineItemType}`}
                >
                  {index > 0 && <Divider />}
                  <AppFlexbox
                    style={{
                      gap: 10,
                      padding: '8px 0px',
                      alignItems: 'center',
                      flexWrap: 'nowrap'
                    }}
                  >
                    <AppImage
                      fit="contain"
                      height={36}
                      src={info.image}
                      width={36}
                    />
                    <AppStack style={{ flex: 1, gap: 0, minWidth: 0 }}>
                      <AppText style={{ fontSize: 13 }} weight={500}>
                        {info.name}
                      </AppText>
                      <AppText style={{ fontSize: 12, color: '#666' }}>
                        {[info.type, info.description]
                          .filter((d) => d)
                          .join(' • ')}
                      </AppText>
                    </AppStack>
                    <AppStack style={{ gap: 0, alignItems: 'end' }}>
                      <AppText
                        style={{ fontSize: 13, whiteSpace: 'nowrap' }}
                        weight={500}
                      >
                        <NumberFormat
                          decimalScale={2}
                          displayType="text"
                          fixedDecimalScale
                          prefix="$"
                          thousandSeparator
                          value={lineItem.subtotalInCents / 100}
                        />
                      </AppText>
                      {lineItem.quantity > 1 && (
                        <AppText style={{ fontSize: 12, color: '#666' }}>
                          Qty {lineItem.quantity}
                        </AppText>
                      )}
                    </AppStack>
                  </AppFlexbox>
                </React.Fragment>
              );
            })}
          </AppStack>
        </Collapse>
      )}
    </AppStack>
  );
};

PaymentPlanOrderSummary.propTypes = {
  isLoading: PropTypes.bool,
  pkRegCart: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  regCart: PropTypes.object
};

export default PaymentPlanOrderSummary;
